import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Tooltip, Typography, useTheme } from "@mui/material";
import { faBan, faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";

import { Sensor } from "@core/apis/shared";

import { heatRangeColors } from "../theme-constants";
import { SensorReading } from "./SensorOverview";

const getBackgroundColor = (
  value: number | null | undefined,
  targetTemperature: number | undefined | null,
  {
    maxColor,
    targetColor,
    minColor,
  }: {
    maxColor: string | null;
    targetColor: string;
    minColor: string | null;
  },
  defaultColor: string
) => {
  if (value == null || isNaN(value)) return defaultColor;
  if (targetTemperature == null || !maxColor || !minColor) return targetColor;
  // Round to nearest 0.5
  const roundedValue = Math.round(value * 2) / 2;

  const range = 3;
  const min = targetTemperature - range;
  const max = targetTemperature + range;

  // Check boundaries
  if (roundedValue <= min) return minColor;
  if (roundedValue >= max) return maxColor;
  if (roundedValue === targetTemperature) return targetColor;

  // Interpolate between min and target color for values below target
  if (roundedValue < targetTemperature) {
    const subRatio = (roundedValue - min) / (targetTemperature - min);
    const r = Math.round(
      parseInt(minColor.slice(1, 3), 16) +
        subRatio * (parseInt(targetColor.slice(1, 3), 16) - parseInt(minColor.slice(1, 3), 16))
    );
    const g = Math.round(
      parseInt(minColor.slice(3, 5), 16) +
        subRatio * (parseInt(targetColor.slice(3, 5), 16) - parseInt(minColor.slice(3, 5), 16))
    );
    const b = Math.round(
      parseInt(minColor.slice(5, 7), 16) +
        subRatio * (parseInt(targetColor.slice(5, 7), 16) - parseInt(minColor.slice(5, 7), 16))
    );
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  // Interpolate between target and max color for values above target
  const subRatio = (roundedValue - targetTemperature) / (max - targetTemperature);
  const r = Math.round(
    parseInt(targetColor.slice(1, 3), 16) +
      subRatio * (parseInt(maxColor.slice(1, 3), 16) - parseInt(targetColor.slice(1, 3), 16))
  );
  const g = Math.round(
    parseInt(targetColor.slice(3, 5), 16) +
      subRatio * (parseInt(maxColor.slice(3, 5), 16) - parseInt(targetColor.slice(3, 5), 16))
  );
  const b = Math.round(
    parseInt(targetColor.slice(5, 7), 16) +
      subRatio * (parseInt(maxColor.slice(5, 7), 16) - parseInt(targetColor.slice(5, 7), 16))
  );
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const SensorCard: React.FC<{
  sensor: Sensor;
  lastReading: SensorReading | null;
  isSelected?: boolean;
  targetTemperature?: number | null;
  onSelect?: (sensorId: string) => void;
}> = ({ sensor, lastReading, isSelected = false, onSelect, targetTemperature }) => {
  const theme = useTheme();
  const { t } = useTranslation(["propai_properties", "_common"]);

  const defaultColor = theme.palette.divider;

  const backgroundColorPalette = useMemo(() => {
    if (targetTemperature == null)
      return {
        maxColor: null,
        targetColor: theme.palette.background.default,
        minColor: null,
      };

    return {
      maxColor: heatRangeColors.high,
      targetColor: heatRangeColors.medium,
      minColor: heatRangeColors.low,
    };
  }, [targetTemperature]);

  const { isOutdated, hoursOutdated } = useMemo(() => {
    if (!lastReading) return { isOutdated: false, hoursOutdated: 0 };
    const readingTime = DateTime.fromISO(lastReading.timestamp);
    const now = DateTime.now();
    const hoursDiff = now.diff(readingTime, "hours").hours;
    return {
      isOutdated: hoursDiff > 2,
      hoursOutdated: Math.floor(hoursDiff),
    };
  }, [lastReading]);

  const tooltipContent = useMemo(() => {
    if (!lastReading)
      return <Typography variant="bodyMedium">{t("sensor_tooltip_no_recent_data")}</Typography>;
    return (
      <Box display="flex" flexDirection="column">
        <Typography variant="labelLarge" display="block">
          {t("sensor_tooltip_last_updated")}:
        </Typography>
        <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
          <Typography variant="bodyMedium">
            {DateTime.fromISO(lastReading.timestamp).toFormat("yyyy-MM-dd HH:mm")}
          </Typography>
          {isOutdated && (
            <FontAwesomeIcon icon={faExclamationCircle} fontSize="small" color="warning" />
          )}
        </Box>
        {isOutdated && (
          <Typography
            variant="bodyMedium"
            display="block"
            sx={{ color: theme.palette.warning.main }}
          >
            {t("sensor_tooltip_outdated_warning", { hours: hoursOutdated })}
          </Typography>
        )}
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("sensor_tooltip_building")}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {sensor.street_address || t("sensor_tooltip_unknown")}
        </Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("apartment")}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {sensor.apartment || t("sensor_tooltip_unknown")}
        </Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("floor")}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {sensor.floor || t("text_na", { ns: "_common" })}
        </Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("text_temperature", { ns: "_common" })}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {lastReading.values?.living_air_temperature?.toFixed(2) ??
            t("text_na", { ns: "_common" })}
          °C
        </Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("text_humidity", { ns: "_common" })}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {lastReading.values?.indoor_relative_humidity?.toFixed(2) ??
            t("text_na", { ns: "_common" })}
          %
        </Typography>
        <Typography variant="labelLarge" display="block" mt={0.5}>
          {t("sensor_tooltip_sensor_label")}:
        </Typography>
        <Typography variant="bodyMedium" display="block">
          {sensor.external_id}
        </Typography>
      </Box>
    );
  }, [lastReading, sensor, isOutdated, hoursOutdated, theme, t]);

  const handleClick = () => {
    if (onSelect) {
      onSelect(sensor.uid);
    }
  };

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: theme.palette.inverseSurface },
        },
      }}
    >
      <Badge color="warning" badgeContent={isOutdated ? "!" : undefined} overlap="circular">
        <Box
          onClick={handleClick}
          sx={{
            width: "80px",
            height: "80px",
            borderRadius: 1,
            border: isSelected
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.background.paper}`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: lastReading
              ? getBackgroundColor(
                  lastReading.values?.living_air_temperature,
                  targetTemperature,
                  backgroundColorPalette,
                  defaultColor
                )
              : defaultColor,
            cursor: "pointer",
            position: "relative",
            "&:hover": {
              backgroundColor: theme.palette.grey[200],
            },
          }}
        >
          {sensor.enabled === false && (
            <FontAwesomeIcon
              icon={faBan}
              style={{
                fontSize: "14px",
                color: theme.palette.text.primary,
                marginBottom: "2px",
              }}
            />
          )}
          {lastReading ? (
            <>
              <Typography variant="bodyLarge" color="text.primary" textAlign="center">
                {lastReading.values?.living_air_temperature != null
                  ? `${lastReading.values.living_air_temperature.toFixed(1)}°C`
                  : "N/A"}
              </Typography>
              <Typography variant="bodySmall" color="text.primary" textAlign="center">
                {sensor.apartment || "N/A"}
              </Typography>
            </>
          ) : (
            <Typography variant="bodyLarge" color="text.primary" textAlign="center">
              Missing
            </Typography>
          )}
        </Box>
      </Badge>
    </Tooltip>
  );
};

export default SensorCard;
